import { useEffect } from 'react'
import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'

import { AccountControls } from '../components/AccountControls'
import AppErrorState from '../components/AppErrorState'
import { CommandPalette } from '../components/CommandPalette'
import Footer from '../components/Footer'
import { Navbar } from '../components/Navbar'
import ToastRegion from '../components/ToastRegion'
import { useAuth } from '../lib/auth/AuthProvider'
import { useTheme } from '../lib/hooks/useTheme'
import PageNotFound from '../pages/PageNotFound'

export const isAdminPath = (pathname: string) =>
  (pathname === '/settings' || pathname.startsWith('/settings/')) &&
  pathname.replace(/\/$/, '') !== '/settings/changelog'

function RootComponent() {
  useTheme()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const { user, status } = useAuth()
  const isLoginPage = pathname === '/login'
  const needsLogin = status === 'ready' && !isLoginPage && !user
  const accessDenied = status === 'ready' && !isLoginPage && !!user && !user.isAdmin && isAdminPath(pathname)

  useEffect(() => {
    if (needsLogin) void navigate({ to: '/login', search: { returnTo: pathname } })
  }, [navigate, needsLogin, pathname])

  if (status === 'loading' && !isLoginPage) return null

  if (needsLogin) return null

  if (isLoginPage) {
    return (
      <>
        <Outlet />
        <ToastRegion />
      </>
    )
  }

  return (
    <>
      <Navbar />
      <AccountControls />
      <main>{accessDenied ? <AppErrorState denied /> : <Outlet />}</main>
      <Footer />
      <ToastRegion />
      <CommandPalette />
    </>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: PageNotFound,
  errorComponent: ({ error, reset }) => <AppErrorState error={error} onRetry={reset} />
})
